/**
 * usePreferences Hook
 * Provides user preferences state and operations.
 * Uses the PreferencesService via the service layer for all data operations.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { UserPreferences, ChartPreferences } from '../types/preferences';
import { DEFAULT_PREFERENCES } from '../types/preferences';
import { useServices } from './useServices';
import { clientLogger } from '@/lib/clientLogger';

interface UsePreferencesReturn {
  preferences: UserPreferences;
  isLoading: boolean;
  updateTheme: (theme: 'dark' | 'light') => Promise<void>;
  updateLastRoute: (route: string) => Promise<void>;
  updateChartPreferences: (chart: Partial<ChartPreferences>) => Promise<void>;
  savePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
}

const PREFERENCES_CACHE_KEY_PREFIX = 'stellartrail:preferences';

function getPreferencesCacheKey(userId: string): string {
  return `${PREFERENCES_CACHE_KEY_PREFIX}:${userId}`;
}

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof Error && error.name === 'AbortError') return true;
  if (error instanceof Error && error.message.includes('signal is aborted')) return true;
  return false;
}

function normalizePreferences(preferences: Partial<UserPreferences>): UserPreferences {
  return {
    ...DEFAULT_PREFERENCES,
    ...preferences,
    chart: {
      ...DEFAULT_PREFERENCES.chart,
      ...preferences.chart,
    },
  };
}

function readCachedPreferences(userId: string): UserPreferences | null {
  if (typeof window === 'undefined') return null;

  try {
    const rawPreferences = window.localStorage.getItem(getPreferencesCacheKey(userId));
    if (!rawPreferences) return null;

    return normalizePreferences(JSON.parse(rawPreferences) as Partial<UserPreferences>);
  } catch (error) {
    clientLogger.warn('[usePreferences] Failed to read cached preferences', error);
    return null;
  }
}

function writeCachedPreferences(userId: string, preferences: UserPreferences): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      getPreferencesCacheKey(userId),
      JSON.stringify(normalizePreferences(preferences)),
    );
  } catch (error) {
    clientLogger.warn('[usePreferences] Failed to cache preferences', error);
  }
}

export const usePreferences = (): UsePreferencesReturn => {
  const { user } = useAuth();
  const { preferencesService } = useServices();

  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const loadPreferences = async () => {
      if (!user || !preferencesService) {
        if (isActive) {
          setPreferences(DEFAULT_PREFERENCES);
          setIsLoading(false);
        }
        return;
      }

      const cachedPreferences = readCachedPreferences(user.uid);
      if (cachedPreferences && isActive) {
        setPreferences(cachedPreferences);
      }

      if (isActive) {
        setIsLoading(true);
      }

      try {
        const prefs = await preferencesService.getPreferences(user.uid);
        if (!isActive) return;

        const normalizedPreferences = normalizePreferences(prefs);
        setPreferences(normalizedPreferences);
        writeCachedPreferences(user.uid, normalizedPreferences);
      } catch (error) {
        if (!isActive) return;

        if (!isAbortError(error)) {
          clientLogger.error('Failed to load preferences:', error);
          setPreferences(cachedPreferences ?? DEFAULT_PREFERENCES);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadPreferences();

    return () => {
      isActive = false;
    };
  }, [user, preferencesService]);

  const savePreferences = useCallback(async (updates: Partial<UserPreferences>) => {
    const previousPreferences = preferences;
    const newPrefs = normalizePreferences({ ...preferences, ...updates });
    setPreferences(newPrefs); // Optimistic update
    if (user) {
      writeCachedPreferences(user.uid, newPrefs);
    }

    if (!user || !preferencesService) return;

    try {
      await preferencesService.savePreferences(user.uid, newPrefs);
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      clientLogger.error('Failed to save preferences:', error);
      setPreferences(previousPreferences); // Revert on error
      writeCachedPreferences(user.uid, previousPreferences);
    }
  }, [user, preferences, preferencesService]);

  const updateTheme = useCallback(async (theme: 'dark' | 'light') => {
    await savePreferences({ theme });
  }, [savePreferences]);

  const updateLastRoute = useCallback(async (route: string) => {
    await savePreferences({ lastRoute: route });
  }, [savePreferences]);

  const updateChartPreferences = useCallback(async (chart: Partial<ChartPreferences>) => {
    const newChart = { ...preferences.chart, ...chart };
    await savePreferences({ chart: newChart });
  }, [savePreferences, preferences.chart]);

  return {
    preferences,
    isLoading,
    updateTheme,
    updateLastRoute,
    updateChartPreferences,
    savePreferences,
  };
};
